import React, { useCallback, useState } from 'react';
import { useApi } from '../../classes/appApi';
import { useOffline } from '../../components/App/AppOfflineContext';
import { useMainLayoutContext } from '../../components/MainLayout';
import { TimelogClockInOutRequest } from '../../defs/defs';
import EqxRequestHandler from '../../shared/components/EqxRequestHandler/EqxRequestHandler';
import useApiEndpoint from '../../shared/hooks/ApiEndpoint';
import useLocation from '../hooks/Location';
import EqxAlert from './EqxAlert/EqxAlert';
import EqxButton from './EqxButton/EqxButton';
import EqxClock from './EqxClock';

const ClockInOut: React.FC = () => {
    const api = useApi();
    const { location, status: locationStatus } = useLocation();
    const [showLocationError, setShowLocationError] = useState(false);
    const mainLayoutContext = useMainLayoutContext();
    const { isOffline } = useOffline();
    const {
        response: status,
        loading,
        error,
        reset
    } = useApiEndpoint(api, api.endpoints.timelogStatus());

    const {
        addSuccessMessage: showGlobalSuccess,
        addErrorMessage: showGlobalError,
        addLoadingMessage: showGlobalLoading,
    } = mainLayoutContext.messages

    const handleClock = useCallback(
        (type: 'in' | 'out') => {
            if (!location || locationStatus !== 'success') {
                // Optionally show a toast or error message
                setShowLocationError(true);
                return;
            }
            const req: TimelogClockInOutRequest = {
                timestamp: new Date().toISOString(),
                location: { latitude: location.lat, longitude: location.lng },
            };
            const endpoint = type === 'in' ? api.endpoints.timelogClockIn() : api.endpoints.timelogClockOut();
            return endpoint.execute(api, req)
                .then(() => {
                    reset();
                    showGlobalSuccess('Clocked ' + (type === 'in' ? 'in' : 'out') + ' successfully');
                    mainLayoutContext.resetLayoutParams(false);
                }).catch(err => {
                    showGlobalError('Error clocking ' + (type === 'in' ? 'in' : 'out') + '. Please try again.');
                    throw err;
                });
        },
        [api, location, locationStatus, reset, mainLayoutContext]
    );

    return (
        <EqxRequestHandler
            state={{ loading, error }}
            content={() => (
                <div className="clock-in-out-panel">
                    {showLocationError && <EqxAlert variant="danger" icon="fas fa-exclamation-circle">Location is required to clock in or out, and is not available. Please check your device location settings.</EqxAlert>}
                    {status?.clockedIn && status.timelog?.start ? (
                        <>
                            <EqxClock startTime={status.timelog.start} />
                            <div className="mb-2 text-muted text-end">Clocked in since: <strong>{new Date(status.timelog.start).toLocaleString()}</strong></div>
                            <EqxButton disabled={isOffline} variant="danger" className="w-100" size="lg" onClick={() => handleClock('out')}>
                                Clock Out
                            </EqxButton>
                        </>
                    ) : (
                        <EqxButton disabled={isOffline} variant="success" size="lg" className="w-100" onClick={() => handleClock('in')}>
                            Clock In
                        </EqxButton>
                    )}
                </div>
            )}
        />
    );
};

export default ClockInOut; 